import { useState, useRef, useEffect } from 'react'
import { ChevronDown, ChevronUp, Info, Star, X } from 'lucide-react'
import Modal from '../UI/Modal'
import { useT } from '../../hooks/useTranslation'
import { useDashboardStore } from '../../store/dashboardStore'
import { getExchangeBalance } from '../../services/api'
import { botApi } from '../../services/botApi'
// Слоты ботов (тип счёта × биржа) — правило доступности в одном месте
// (см. src/utils/botSlots.js): демо-бот на Bybit НЕ запрещает демо-бота на
// Binance, это разные слоты (2026-09-20).
import {
  EXCHANGE_LABEL, exchangeIdOf, slotTaken, initialSlot, switchAccountMode,
  takenExchanges as takenExchangesFor, freeExchanges as freeExchangesFor,
  modeAvailable, bookableExchanges,
} from '../../utils/botSlots'
// Режим биржи из админки: «в разработке» и скрытую биржу НЕЛЬЗЯ предлагать при
// создании бота (2026-10-26, жалоба владельца: Binance стояла «в разработке», а
// форма всё равно давала создать на ней бота).
import { isExchangeBookable, isExchangeVisible, exchangeModeOf } from '../../utils/exchangeMode'
// Порог фандинга вводится без минуса: знак нарисован слева и не стирается,
// а в поле лежит модуль числа (2026-10-21, см. src/utils/fundingThreshold.js).
import { fundingMagnitude, fundingDraft, fundingSigned, parseThresholdRange } from '../../utils/fundingThreshold'

const EXCHANGES = [
  { id: 'Bybit', label: 'Bybit' },
  // Binance (2026-09-17, task.txt): та же схема, что Bybit — свои ключи на
  // биржу, свой фандинг и двигатель. Список пар в EXCHANGE_PAIRS уже есть.
  { id: 'Binance', label: 'Binance' },
  // Bitget (2026-09-17, task.txt п.3): полноценный бот, как Bybit. При
  // подключении ключей к этой бирже обязательно поле passphrase (API v2).
  { id: 'Bitget', label: 'Bitget' },
]

// Futures pairs per exchange
const EXCHANGE_PAIRS = {
  Bybit: [
    'BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT', 'DOGEUSDT',
    'ADAUSDT', 'MATICUSDT', 'LINKUSDT', 'AVAXUSDT', 'LTCUSDT',
    'OPUSDT', 'ARBUSDT', 'DOTUSDT', 'ATOMUSDT', 'UNIUSDT',
    'FILUSDT', 'APTUSDT', 'SUIUSDT', 'INJUSDT', 'TIAUSDT',
    'TONUSDT', 'WIFUSDT', 'PEPEUSDT', 'BONKUSDT', 'SEIUSDT',
    'JUPUSDT', 'PYTHUSDT', 'NEARUSDT', 'FTMUSDT', 'SANDUSDT',
  ],
  Binance: [
    'BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT', 'DOGEUSDT',
    'ADAUSDT', 'MATICUSDT', 'LINKUSDT', 'AVAXUSDT', 'LTCUSDT',
    'OPUSDT', 'ARBUSDT', 'DOTUSDT', 'ATOMUSDT', 'UNIUSDT',
    'FILUSDT', 'APTUSDT', 'SUIUSDT', 'INJUSDT', 'TIAUSDT',
    'TONUSDT', 'WIFUSDT', 'PEPEUSDT', 'BONKUSDT', 'SEIUSDT',
    'JUPUSDT', 'PYTHUSDT', 'NEARUSDT', 'FTMUSDT', 'SANDUSDT',
  ],
  // Bitget (2026-09-17, task.txt п.3): фьючерсные пары USDT-FUTURES, тот же
  // рабочий набор мейджоров, что у Binance (все торгуются на Bitget).
  Bitget: [
    'BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT', 'DOGEUSDT',
    'ADAUSDT', 'MATICUSDT', 'LINKUSDT', 'AVAXUSDT', 'LTCUSDT',
    'OPUSDT', 'ARBUSDT', 'DOTUSDT', 'ATOMUSDT', 'UNIUSDT',
    'FILUSDT', 'APTUSDT', 'SUIUSDT', 'INJUSDT', 'TIAUSDT',
    'TONUSDT', 'WIFUSDT', 'PEPEUSDT', 'BONKUSDT', 'SEIUSDT',
    'JUPUSDT', 'PYTHUSDT', 'NEARUSDT', 'FTMUSDT', 'SANDUSDT',
  ],
}

const LEVERAGE_OPTIONS = [1, 2, 3, 5, 10]

// Минимальный рабочий объём (USDT): объём меньше бот не должен торговать —
// как на фронте (форма создания), так и на бэкенде (api.js).
const MIN_GLOBAL_VOLUME = 10

const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: 12,
  background: '#141414', border: '1px solid rgba(255,255,255,0.08)',
  color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box',
}

function Field({ label, children, noMargin, tooltip }) {
  const [showTip, setShowTip] = useState(false)
  return (
    <div style={{ marginBottom: noMargin ? 0 : 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {tooltip && (
          /* Wrapper: hover on icon OR card keeps tooltip visible */
          <div
            style={{ position: 'relative', flexShrink: 0 }}
            onMouseEnter={() => setShowTip(true)}
            onMouseLeave={() => setShowTip(false)}
          >
            <button
              type="button"
              onClick={() => setShowTip(s => !s)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '2px', display: 'flex', alignItems: 'center',
              }}
            >
              <Info size={15} color={showTip ? '#34D399' : '#636366'} />
            </button>

            {showTip && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 6px)',
                left: 0,
                width: 210,
                background: '#1C1C1E',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12,
                padding: '10px 12px',
                fontSize: 11, color: '#8E8E93', lineHeight: 1.6,
                zIndex: 400,
                boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
              }}>
                {tooltip}
              </div>
            )}
          </div>
        )}
        <span style={{
          fontSize: 11, color: '#8E8E93', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          {label}
        </span>
      </div>
      {children}
    </div>
  )
}

function ExchangeDropdown({ value, onChange, takenExchanges, appConfig, t }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const h = e => { if (!ref.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  const selected = EXCHANGES.find(e => e.id === value)
  // Скрытые админом биржи ('off') в списке не показываем вовсе, а биржи «в
  // разработке» ('dev') показываем, но неактивными и с подписью — так же, как
  // карточка в разделе «Биржи» (2026-10-26).
  const visible = EXCHANGES.filter(ex => isExchangeVisible(appConfig, exchangeIdOf(ex.id)))

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          ...inputStyle, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', cursor: 'pointer',
          border: open ? '1px solid rgba(255,107,53,0.45)' : '1px solid rgba(255,255,255,0.08)',
          transition: 'border 0.2s',
        }}
      >
        <span style={{ color: '#fff' }}>{selected?.label ?? '—'}</span>
        <ChevronDown size={15} color="#636366"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}
        />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 5px)', left: 0, right: 0,
          background: '#222', borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 200, overflow: 'hidden',
        }}>
          {visible.map((ex, i) => {
            // takenExchanges приходит каноническими id бирж ('bybit'), а в
            // дропдауне id — display-имена ('Bybit'), поэтому сравниваем через
            // exchangeIdOf. Список занятых считается по ВЫБРАННОМУ типу счёта:
            // демо-бот на Bybit не мешает создать демо-бота на Binance.
            const exId = exchangeIdOf(ex.id)
            const taken = takenExchanges.includes(exId)
            // «В разработке» — создать бота нельзя (ключи на такую биржу тоже не
            // подключаются), поэтому пункт неактивен с подписью.
            const dev = exchangeModeOf(appConfig, exId) === 'dev'
            const disabled = taken || dev
            const isSel = value === ex.id
            return (
              <button
                key={ex.id}
                type="button"
                disabled={disabled}
                onClick={() => { if (!disabled) { onChange(ex.id); setOpen(false) } }}
                style={{
                  width: '100%', padding: '12px 14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: isSel ? 'rgba(255,107,53,0.1)' : 'transparent',
                  border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
                  borderBottom: i < visible.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600, color: disabled ? '#3A3A3A' : '#fff' }}>
                  {ex.label}
                </span>
                {taken && (
                  <span style={{ fontSize: 10, color: '#3A3A3A' }}>{t.botsExchangeTaken}</span>
                )}
                {dev && !taken && (
                  <span style={{ fontSize: 10, color: '#3A3A3A' }}>{t.botsInDev}</span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function NumberInput({ value, onChange, placeholder, min, max, step }) {
  const numStep = step ?? 1

  const increment = () => {
    const next = Math.round((Number(value || 0) + numStep) * 10000) / 10000
    onChange(String(max != null ? Math.min(max, next) : next))
  }
  const decrement = () => {
    const next = Math.round((Number(value || 0) - numStep) * 10000) / 10000
    onChange(String(min != null ? Math.max(min, next) : next))
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        style={{ ...inputStyle, paddingRight: 38 }}
      />
      <div style={{
        position: 'absolute', right: 1, top: 1, bottom: 1,
        width: 34, display: 'flex', flexDirection: 'column',
        borderLeft: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '0 11px 11px 0', overflow: 'hidden',
      }}>
        <button
          type="button"
          onClick={increment}
          style={{
            flex: 1, background: 'rgba(255,255,255,0.04)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,107,53,0.14)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
        >
          <ChevronUp size={11} color="#636366" />
        </button>
        <button
          type="button"
          onClick={decrement}
          style={{
            flex: 1, background: 'rgba(255,255,255,0.04)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,107,53,0.14)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
        >
          <ChevronDown size={11} color="#636366" />
        </button>
      </div>
    </div>
  )
}

function SectionDivider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 14px' }}>
      <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: '#636366', letterSpacing: 0.6, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
    </div>
  )
}

function BlacklistField({ exchange, value, onChange, t }) {
  const [input, setInput] = useState('')
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const allPairs = EXCHANGE_PAIRS[exchange] ?? []
  const filtered = allPairs.filter(p =>
    p.toLowerCase().includes(input.toLowerCase()) && !value.includes(p)
  )

  const addPair = (pair) => {
    if (!value.includes(pair)) onChange([...value, pair])
    setInput('')
    setOpen(false)
  }

  const removePair = (pair) => onChange(value.filter(p => p !== pair))

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault()
      const exact = allPairs.find(p => p.toLowerCase() === input.trim().toLowerCase())
      addPair(exact ?? input.trim().toUpperCase())
    }
    if (e.key === 'Escape') setOpen(false)
  }

  useEffect(() => {
    if (!open) return
    const h = e => { if (!ref.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  return (
    <div ref={ref}>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {value.map(pair => (
            <div key={pair} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 8,
              background: 'rgba(255,59,48,0.1)',
              border: '1px solid rgba(255,59,48,0.25)',
            }}>
              <span style={{ fontSize: 12, color: '#FF3B30', fontWeight: 600 }}>{pair}</span>
              <button
                type="button"
                onClick={() => removePair(pair)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '0 1px', display: 'flex', lineHeight: 1,
                }}
              >
                <X size={10} color="#FF3B30" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={input}
          onChange={e => { setInput(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t.addBotBlacklistPh}
          style={{ ...inputStyle }}
        />

        {open && filtered.length > 0 && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 5px)', left: 0, right: 0,
            background: '#222', borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            zIndex: 300, maxHeight: 180, overflowY: 'auto',
          }}>
            {filtered.slice(0, 8).map((pair, i) => (
              <button
                key={pair}
                type="button"
                onClick={() => addPair(pair)}
                style={{
                  width: '100%', padding: '10px 14px',
                  textAlign: 'left', fontSize: 13, color: '#fff',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  borderBottom: i < Math.min(filtered.length, 8) - 1
                    ? '1px solid rgba(255,255,255,0.05)' : 'none',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,107,53,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {pair}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function TradingModeToggle({ mode, onChange, onCustomModeChange, setLeverage, setOrdersCount, setMinFundingRate, setGlobalTp, setGlobalSl, t }) {
  const btnStyle = (active, color) => ({
    flex: 1, padding: '10px 0', borderRadius: 10,
    fontSize: 13, fontWeight: 700, border: 'none',
    cursor: 'pointer', transition: 'all 0.2s',
    background: active ? color : 'rgba(255,255,255,0.05)',
    color: active ? '#fff' : '#636366',
  })

  // Функция переключения: если уже активно — выключаем (null), иначе включаем
  const toggleMode = (selectedMode) => {
    if (mode === selectedMode) {
      onChange(null) // Выключение режима (кнопка тухнет)
      onCustomModeChange(false) // Отключаем пользовательский режим
    } else {
      onChange(selectedMode) // Включение режима (кнопка горит)
      onCustomModeChange(true) // Включаем пользовательский режим
    }
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <span style={{
        display: 'block', fontSize: 11, color: '#8E8E93', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
      }}>
        {t.addBotTradingMode}
      </span>
      <p style={{ fontSize: 11, color: '#636366', margin: '0 0 8px', lineHeight: 1.5 }}>
        {t.tradingModeDesc}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => toggleMode('safe')}
          style={{
            flex: 1,
            padding: '12px',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            background: mode === 'safe'
              ? 'linear-gradient(135deg, #34D399 0%, #2ea84a 100%)'
              : 'rgba(255,255,255,0.05)',
            color: mode === 'safe' ? '#fff' : '#636366',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          {mode === 'safe' ? t.tradingModeSafe + ' ✓' : t.tradingModeSafe}
        </button>
      </div>
    </div>
  )
}

// Переключатель направления сделки (long/short) при создании бота (п.7).
// Кнопки — компактные, как кнопки режима счёта (DEMO/REAL): в первой «long»
// (сбор фандинга), во второй «short» (сбор падений). Активная long — зелёная,
// активная short — серая (как кнопка DEMO). Под каждой кнопкой — значок «i»
// с объяснением конкретного режима (по нажатию).
// Отключённые админом направления (task.txt п.7) — не рисуем вообще: выбрать
// их нельзя, а API всё равно вернул бы 400.
function DirectionToggle({ value, onChange, t, recommended, longEnabled = true, shortEnabled = true }) {
  const [tip, setTip] = useState(null) // 'long' | 'short' | null
  // Геометрия кнопок — ровно как у кнопок режима счёта (DEMO/REAL) выше:
  // та же ширина (равные flex-колонки), padding '10px 0', fontSize 13,
  // borderRadius 10. width:'100%' растягивает кнопку на всю колонку.
  const btnStyle = (active, color) => ({
    width: '100%', padding: '10px 0', borderRadius: 10,
    fontSize: 13, fontWeight: 700, border: 'none',
    cursor: 'pointer', transition: 'all 0.2s',
    background: active ? color : 'rgba(255,255,255,0.05)',
    color: active ? '#fff' : '#636366',
  })
  // Объяснение режима — обычный блок на всю ширину формы (НЕ absolute).
  // Так он всегда остаётся в пределах окна и текст виден целиком
  // (модалка при необходимости прокручивается вертикально).
  const tipBox = (
    <div style={{
      marginTop: 6, background: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 12, padding: '10px 12px', fontSize: 11, color: '#8E8E93',
      lineHeight: 1.6, boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
    }}>
      {tip === 'long' ? t.tradeModeLongDesc : t.tradeModeShortDesc}
    </div>
  )

  // Доступные направления в фиксированном порядке (long, short).
  const available = [longEnabled && 'long', shortEnabled && 'short'].filter(Boolean)

  return (
    <div style={{ marginBottom: 14 }}>
      <span style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11, color: '#8E8E93', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
      }}>
        {t.addBotTradeDir}
      </span>
      {recommended && longEnabled && shortEnabled && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          marginBottom: 8, fontSize: 11.5, fontWeight: 600,
          color: '#F59E0B', lineHeight: 1.4,
        }}>
          <Star size={13} fill="#F59E0B" color="#F59E0B" style={{ flexShrink: 0 }} />
          <span>
            {t.recommendedMode}: {recommended === 'short' ? t.tradeModeShort : t.tradeModeLong}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        {available.map(m => (
          <div key={m} style={{
            flex: 1, display: 'flex', flexDirection: 'column',
          }}>
            <button
              type="button"
              onClick={() => onChange(m)}
              style={btnStyle(value === m, m === 'long'
                ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
                : 'linear-gradient(135deg, #636366 0%, #48484A 100%)')}
            >
              {m}
            </button>
            {/* «i» — объяснение режима, по центру под кнопкой */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
              <button
                type="button"
                aria-label={m}
                onClick={() => setTip(tip === m ? null : m)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '4px 2px', display: 'inline-flex', alignItems: 'center',
                }}
              >
                <Info size={13} color={tip === m ? '#34D399' : '#636366'} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Объяснение режима — на всю ширину, в границах окна */}
      {tip && tipBox}
    </div>
  )
}

const ORDER_COUNTS = [1, 2, 3, 4, 5]

const pctSuffix = {
  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
  fontSize: 12, color: '#636366', fontWeight: 600, pointerEvents: 'none',
}

export default function AddBotModal({ defaultType, defaultAccountMode, onClose }) {
  const modalRef = useRef(null)
  const t = useT()
  const addUserBot  = useDashboardStore(s => s.addUserBot)
  const setUserBots = useDashboardStore(s => s.setUserBots)
  const fetchUserBots = useDashboardStore(s => s.fetchUserBots)
  const userBots    = useDashboardStore(s => s.userBots)
  // Доступность типов счёта по подписке: демо — при любой активной подписке,
  // реальный — только при реальной.
  const demoAllowed = useDashboardStore(s => s.demoAllowed)
  const realAllowed = useDashboardStore(s => s.realAllowed)

  // ── Слоты (тип счёта × биржа) ──────────────────────────────────────────
  // У юзера может быть по ОДНОМУ боту на каждом типе счёта и КАЖДОЙ бирже:
  // демо-бот на Bybit не мешает создать демо-бота на Binance (так считает и
  // бэкенд — countBotsByAccount(userId, accountMode, exchange) в POST /api/bots).
  // До 2026-09-20 здесь считалось «демо занято вообще», поэтому подключённые
  // ключи Binance не давали создать демо-бота — жалоба владельца.
  const appConfig = useDashboardStore(s => s.appConfig)
  // Биржи, открытые админом для новых ботов (2026-10-26): «в разработке» и
  // скрытые в список не попадают. Они не участвуют ни в подсчёте свободных
  // слотов, ни в выборе начальной биржи — иначе форма открылась бы на бирже,
  // где создать бота нельзя (ровно на это жаловался владелец: Binance стояла
  // «в разработке», а создать бота на ней форма всё равно давала).
  const bookableIds = bookableExchanges(appConfig)
  const demoFree = freeExchangesFor(userBots, 'demo', bookableIds)
  const realFree = freeExchangesFor(userBots, 'real', bookableIds)
  const freeFor  = (mode) => (mode === 'demo' ? demoFree : realFree)
  const allowedFlags = { demoAllowed, realAllowed, exchanges: bookableIds }
  // Тип счёта доступен, если подписка его открывает И есть хоть одна свободная
  // биржа (иначе бота некуда создать — все слоты этого типа заняты).
  const availFor = (mode) => modeAvailable(userBots, mode, allowedFlags)
  const demoAvail = availFor('demo')
  const realAvail = availFor('real')

  const [tradingMode, setTradingMode] = useState(null)
  // Направление сделки: 'long' — сбор фандинга, 'short' — сбор падения/отката
  // после выплаты фандинга (перевёрнутый алгоритм). По умолчанию выбирается
  // режим, который админ выставил как рекомендуемый (по умолчанию 'short' —
  // рынок часто нацелен на падение). Над кнопками юзеру показывается подсказка
  // «Рекомендуется: …».
  const recommendedMode = appConfig?.recommendedTradeMode === 'long' ? 'long' : 'short'
  // Режимы торговли (task.txt п.7): направления, включённые админом. Если
  // рекомендуемое отключено — автоматически подставляем оставшееся
  // разрешённое; отключены оба — форма не даст создать бота (бэкенд 400).
  const tradeModes = appConfig?.tradeModes || {}
  const longModeEnabled = tradeModes.longEnabled !== false
  const shortModeEnabled = tradeModes.shortEnabled !== false
  const enabledModes = [longModeEnabled && 'long', shortModeEnabled && 'short'].filter(Boolean)
  const initialMode = enabledModes.includes(recommendedMode)
    ? recommendedMode
    : (enabledModes[0] || recommendedMode)
  const [tradeMode, setTradeMode] = useState(initialMode)
  // Начальная пара «тип счёта + биржа» (см. initialSlot в botSlots.js): тип из
  // defaultAccountMode (кнопка создания в контексте типа), иначе реальный →
  // демо, и первая СВОБОДНАЯ биржа этого типа. Свободных слотов у типа нет —
  // кнопка типа будет неактивна, а под ней появится причина.
  const initial = initialSlot(userBots, { ...allowedFlags, prefer: defaultAccountMode })
  const [accountMode, setAccountMode] = useState(initial.accountMode)
  const [isCustomMode, setIsCustomMode] = useState(false) // Режим пользовательских настроек
  const [name, setName]         = useState('')
  // Показываем подсказку, когда пользователь ввёл не-английский символ
  // (он молча отфильтровывается — в поле остаются только английские буквы,
  // цифры и пробелы).
  const [nameEnError, setNameEnError] = useState(false)
  const [exchange, setExchange] = useState(EXCHANGE_LABEL[initial.exchange])
  // Производные от ВЫБРАННОГО типа счёта и биржи — объявляются ПОСЛЕ их
  // useState (иначе TDZ: обращение к `exchange`/`accountMode` до объявления
  // роняет рендер формы — ReferenceError «Cannot access before initialization»).
  // Свободен ли слот на ТЕКУЩЕЙ выбранной бирже (для подписи под кнопками).
  const demoAvailHere = !slotTaken(userBots, 'demo', exchange)
  const realAvailHere = !slotTaken(userBots, 'real', exchange)
  // Биржи, занятые на ВЫБРАННОМ типе счёта, — в дропдауне они неактивны.
  const takenExchangeIds = takenExchangesFor(userBots, accountMode)
  const exchangeLabelNow = EXCHANGE_LABEL[exchangeIdOf(exchange)]
  const [leverage, setLeverage] = useState(() => {
    const lv = (appConfig?.botDefaults || {})[initialMode === 'short' ? 'short' : 'long']?.Leverage
    return lv !== undefined && lv !== null && lv !== '' ? Number(lv) : 10
  })
  const [isCreating, setIsCreating] = useState(false)

  // Баланс USDT на выбранной бирже — грузим с бэкенда
  const [balance, setBalance]       = useState(null)   // null = загрузка, false = ошибка
  const [maxPositions, setMaxPositions] = useState(3)
  // В обоих полях порога лежит МОДУЛЬ значения («0.6»), а минус нарисован
  // слева отдельным знаком: юзер вводит только цифру, стереть знак нельзя.
  // На сервер уходит уже отрицательное число (fundingSigned).
  //
  // Поля пустые, а админский порог по умолчанию показан в них ТУСКЛО
  // (placeholder) — просьба владельца 2026-10-26. Раньше верхняя граница была
  // уже заполнена значением, и понять, что это дефолт из админки, было нельзя.
  const [minFundingRate, setMinFundingRate] = useState('')
  // Макс. ставка финансирования (task.txt, 2026-09-16): необязательное поле,
  // пустое = ограничения нет. Это НИЖНЯЯ граница диапазона ставок (бот не берёт
  // «слишком глубокий» фандинг, ниже указанного значения).
  const [maxFundingRate, setMaxFundingRate] = useState('')
  const [globalVolume, setGlobalVolume] = useState('')
  // Дефолты TP/SL зависят от направления (task.txt) и берутся из админских
  // «Параметров ботов по умолчанию» (task.txt п.6): Long — TP 0.05, SL 0.3;
  // Short — TP 0.3, SL 0.1 (падение после выплаты фандинга). Фолбэки на
  // прежние константы, если в конфиге пока пусто (старый admin_config.json).
  const botDefaults = (appConfig?.botDefaults || {})[tradeMode === 'short' ? 'short' : 'long'] || {}
  // Порог фандинга по умолчанию — из тех же админских «Параметров ботов по
  // умолчанию» (просьба владельца 2026-10-26). Эти значения: (а) показываются
  // ТУСКЛО в пустых полях (placeholder) и (б) уходят на сервер, если юзер поле
  // не заполнил — иначе бот получил бы старые −0.6/null, а не дефолт админки.
  // Верхняя граница в админке может быть пустой («без ограничения») — тогда и
  // подсказки в поле нет, и на сервер уходит null.
  const defaultMinFunding = fundingMagnitude(botDefaults.MinFundingRate, '0.6')
  const defaultMaxFunding = fundingMagnitude(botDefaults.MaxFundingRate, '')
  const defaultMinFundingSigned = fundingSigned(defaultMinFunding, -0.6)
  const defaultMaxFundingSigned = fundingSigned(defaultMaxFunding, null)
  // Диапазон ставок (2026-10-28): верхняя граница — MinFundingRate, нижняя —
  // MaxFundingRate. Если нижняя не глубже верхней (например оба поля −0.5),
  // выборка пуста: бот работает, уведомления приходят, а сделок нет. Такую
  // настройку форма не отправляет (сервер тоже отвечает 400).
  const fundingRange = parseThresholdRange({
    upper: fundingSigned(minFundingRate, defaultMinFundingSigned),
    lower: fundingSigned(maxFundingRate, defaultMaxFundingSigned),
  })
  const fundingRangeError = fundingRange.ok ? '' : fundingRange.error
  const [globalTp, setGlobalTp] = useState(
    String(botDefaults.TakeProfit ?? (tradeMode === 'short' ? '0.3' : '0.05')),
  )
  const [globalSl, setGlobalSl] = useState(
    String(botDefaults.StopLoss ?? (tradeMode === 'short' ? '0.1' : '0.3')),
  )
  const [ordersCount, setOrdersCount] = useState(1) // Количество ордеров
  const [launchError, setLaunchError] = useState('')
  
  // Отслеживаем, выбрал ли пользователь режим кнопкой (для подсветки)
  const [selectedPreset, setSelectedPreset] = useState(null)

  // Загружаем баланс при смене биржи
  useEffect(() => {
    setBalance(null)
    getExchangeBalance(exchange.toLowerCase())
      .then(data => setBalance(data?.available ?? false))
      .catch(() => setBalance(false))
  }, [exchange])

  // Переключение типа счёта (кнопки DEMO / REAL). Если на выбранной бирже такой
  // слот уже занят — switchAccountMode уведёт биржу на свободную того же типа:
  // юзер нажал «DEMO», потому что хочет демо-бота, и упереться в занятую биржу
  // он не должен (ровно на это жаловался владелец: демо-бот на Bybit не давал
  // создать демо-бота на Binance, хотя демо-слот Binance был пуст).
  const applyMode = (mode) => {
    const next = switchAccountMode(userBots, mode, exchange, allowedFlags)
    if (!next) return
    setAccountMode(next.accountMode)
    setExchange(EXCHANGE_LABEL[next.exchange])
  }

  // Флаги подписки и список ботов могут доехать уже после открытия модалки:
  // если выбранный тип на текущей бирже недоступен — уходим на свободный слот;
  // если недоступен сам тип счёта (нет подписки либо все слоты заняты) —
  // переключаемся на доступный тип.
  useEffect(() => {
    if (!availFor(accountMode)) {
      const other = accountMode === 'demo' ? 'real' : 'demo'
      if (availFor(other)) applyMode(other)
      return
    }
    const free = freeFor(accountMode)
    // free пуст — все биржи этого типа заняты/закрыты админом; уводить выбор
    // некуда (прежний код в этом случае записал бы undefined).
    if (free.length && !free.includes(exchangeIdOf(exchange))) setExchange(EXCHANGE_LABEL[free[0]])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountMode, exchange, demoAllowed, realAllowed, demoFree.join(','), realFree.join(',')])

  // Смена направления сделки: вместе с направлением применяем его дефолты
  // TP/SL (task.txt). Источник — админские «Параметры ботов по умолчанию»
  // (п.6): Long — TP 0.05 / SL 0.3; Short — TP 0.3 / SL 0.1 (в шорте тейк-профит
  // 0.3 — наша прибыль после покрытия выплаты фандинга, стоп 0.1 — страховка,
  // ниже риск). Фолбэк на константы, если конфиг ещё не заполнен.
  const handleTradeModeChange = (dir) => {
    setTradeMode(dir)
    const bd = (appConfig?.botDefaults || {})[dir === 'short' ? 'short' : 'long'] || {}
    setGlobalTp(String(bd.TakeProfit ?? (dir === 'short' ? '0.3' : '0.05')))
    setGlobalSl(String(bd.StopLoss ?? (dir === 'short' ? '0.1' : '0.3')))
    // Пороги фандинга у направлений свои: стираем введённое, чтобы в полях
    // снова тускло показывался дефолт НОВОГО направления (2026-10-26).
    setMinFundingRate('')
    setMaxFundingRate('')
  }

  const nameExists = name.trim() !== '' &&
    userBots.some(b => b.name.trim().toLowerCase() === name.trim().toLowerCase())

  // Минимальный объём 10 USDT: меньше (или не число) — нельзя ни оставить,
  // ни создать бота.
  const volumeNum = Number(globalVolume)
  const volumeBelowMin = globalVolume !== '' &&
    (Number.isNaN(volumeNum) || volumeNum < MIN_GLOBAL_VOLUME)
  // Биржа в «разработке» или скрыта админом — создать на ней бота нельзя
  // (бэкенд тоже ответит 403). До такого выбора дело доходить не должно:
  // дропдаун и initialSlot предлагают только открытые биржи. Проверка страхует
  // от случая, когда конфиг админки доехал уже после открытия формы.
  const exchangeLocked = !isExchangeBookable(appConfig, exchangeIdOf(exchange))
  const canSubmit = name.trim() !== '' && !nameExists &&
    globalVolume !== '' && !volumeBelowMin &&
    globalTp !== '' && globalSl !== '' && !exchangeLocked && !fundingRangeError

  // При потере фокуса «добиваем» объём до минимального (10 USDT), чтобы
  // значение меньше минимума (или не число) вообще не могло остаться в поле.
  const clampVolume = () => {
    if (globalVolume === '') return
    const num = Number(globalVolume)
    if (Number.isNaN(num) || num < MIN_GLOBAL_VOLUME) {
      setGlobalVolume(String(MIN_GLOBAL_VOLUME))
    }
  }

  const handleLaunch = async () => {
    if (!canSubmit) return
    setLaunchError('')
    try {
      // ── РЕАЛЬНОЕ СОЗДАНИЕ БОТА ЧЕРЕЗ API ──
      // Если кнопка "Пользовательские" нажата (selectedPreset === 'safe', поля становятся
      // редактируемыми через isCustomMode), сохраняем режим как 'custom'.
      // Если кнопка НЕ нажата — используются системные (safe) значения по умолчанию.
      const finalTradingMode = selectedPreset === 'safe' ? 'custom' : 'safe';

      const params = {
        TakeProfit: Number(globalTp) || 0.3,
        StopLoss: Number(globalSl) || 0.1,
        Volume: Number(globalVolume) || 100,
        Leverage: leverage,
        Token: ordersCount,
        DEMO: accountMode === 'demo' ? "True" : "False",
        REAL: accountMode === 'real' ? "True" : "False",
        TradingMode: finalTradingMode, // Передаем режим на сервер
        // Направление сделки (LONG/SHORT) — передаём на сервер
        TradeDirection: tradeMode === 'short' ? 'SHORT' : 'LONG',
        Blacklist: [],
        // Порог фандинга — ВЕРХНЯЯ граница: бот берёт монеты со ставкой не выше
        // указанной. Пустое поле = дефолт из админки (2026-10-26): раньше
        // подставлялся жёсткий -0.6, и бот не получал админское значение,
        // которое юзер видел тускло в поле. В поле — модуль, поэтому знак
        // минуса возвращаем здесь (fundingSigned).
        MinFundingRate: fundingSigned(minFundingRate, defaultMinFundingSigned),
        // Макс. ставка финансирования — НИЖНЯЯ граница (необязательно).
        // Пустое поле → дефолт админки, а если и он пуст → null (ограничения
        // нет). 0 — валидное число, поэтому пустота обрабатывается явно.
        MaxFundingRate: fundingSigned(maxFundingRate, defaultMaxFundingSigned),
        // Биржа бота (2026-09-17): id из дропдауна ('Bybit'/'Binance'). Сервер
        // нормализует в lowercase-id и хранит display-имя в bots.exchange.
        Exchange: exchange || 'Bybit'
      };

      setIsCreating(true);
      const response = await botApi.createBot(name.trim(), params);
      console.log("[AddBot] Server response:", response);

      // Формируем объект для локального стейта на основе ответа сервера
      const newBot = {
        id: response.bot?.id || Date.now(),
        name: name.trim(),
        type: defaultType,
        exchange: exchange || 'Bybit',
        status: 'stopped', 
        profit: 0,
        closedTrades: 0,
        accountMode: accountMode,
        createdAt: new Date().toISOString(),
        workTime: null,
        pair: null,
        // Параметры внутри algoParams
        algoParams: {
          leverage: params.Leverage || 10,
          orders: ordersCount,
          tradingMode: finalTradingMode, // Сохраняем актуальный режим
          tradeMode: tradeMode === 'short' ? 'short' : 'long', // направление сделки
          globalVolume: params.Volume || 100,
          tp: params.TakeProfit || 0.3,
          sl: params.StopLoss || 0.1,
        }
      };

      console.log("[AddBot] Bot created successfully. Updating UI...");
      
      // 1. Мгновенно добавляем бота в интерфейс для моментального отклика
      addUserBot(newBot);
      
      // 2. В фоне синхронизируем с сервером (чтобы убедиться в актуальности)
      fetchUserBots().finally(() => {
        setIsCreating(false);
        modalRef.current?.close();
      });
    } catch (err) {
      setIsCreating(false);
      setLaunchError(err?.message ?? t.connectionError)
    }
  }

  return (
    <Modal ref={modalRef} title={t.addBotTitle} onClose={onClose}>
      <div>

        {/* ── Account Mode (DEMO / REAL) ── */}
        {/* Тип счёта выбирается ВМЕСТЕ с биржей: слот — это пара (тип × биржа),
            поэтому «DEMO» активен, если есть подписка и существует биржа, где
            демо-бота ещё нет. Занята текущая биржа — кнопка свободна и увезёт
            биржу на свободную (см. applyMode). */}
        <Field label={t.accountMode}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => applyMode('demo')}
              disabled={!demoAvail}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 10,
                fontSize: 13, fontWeight: 700, border: 'none',
                cursor: demoAvail ? 'pointer' : 'not-allowed',
                opacity: demoAvail ? 1 : 0.4,
                transition: 'all 0.2s',
                background: accountMode === 'demo' ? 'linear-gradient(135deg, #636366 0%, #48484A 100%)' : 'rgba(255,255,255,0.05)',
                color: accountMode === 'demo' ? '#fff' : '#636366',
              }}
            >
              DEMO
            </button>
            <button
              type="button"
              onClick={() => applyMode('real')}
              disabled={!realAvail}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 10,
                fontSize: 13, fontWeight: 700, border: 'none',
                cursor: realAvail ? 'pointer' : 'not-allowed',
                opacity: realAvail ? 1 : 0.4,
                transition: 'all 0.2s',
                background: accountMode === 'real' ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)' : 'rgba(255,255,255,0.05)',
                color: accountMode === 'real' ? '#fff' : '#636366',
              }}
            >
              REAL
            </button>
          </div>
          {/* Подписка не открывает тип счёта — это про тариф, не про биржи. */}
          {!realAllowed && (
            <span style={{ display: 'block', fontSize: 11, color: '#8E8E93', marginTop: 6, opacity: 0.85 }}>
              🔒 {t.realAccountLocked}
            </span>
          )}
          {!demoAllowed && (
            <span style={{ display: 'block', fontSize: 11, color: '#8E8E93', marginTop: 6, opacity: 0.85 }}>
              🔒 {t.demoAccountLocked}
            </span>
          )}
          {/* Все биржи заняты на этом типе счёта — создать бота негде. */}
          {realAllowed && realFree.length === 0 && (
            <span style={{ display: 'block', fontSize: 11, color: '#8E8E93', marginTop: 6, opacity: 0.85 }}>
              🔒 {t.realBotAllExchanges}
            </span>
          )}
          {demoAllowed && demoFree.length === 0 && (
            <span style={{ display: 'block', fontSize: 11, color: '#8E8E93', marginTop: 6, opacity: 0.85 }}>
              🔒 {t.demoBotAllExchanges}
            </span>
          )}
          {/* Занята только ТЕКУЩАЯ биржа — остальные свободны. */}
          {realAllowed && realFree.length > 0 && !realAvailHere && (
            <span style={{ display: 'block', fontSize: 11, color: '#8E8E93', marginTop: 6, opacity: 0.85 }}>
              🔒 {t.realBotExists.replace('{exchange}', exchangeLabelNow)}
            </span>
          )}
          {demoAllowed && demoFree.length > 0 && !demoAvailHere && (
            <span style={{ display: 'block', fontSize: 11, color: '#8E8E93', marginTop: 6, opacity: 0.85 }}>
              🔒 {t.demoBotExists.replace('{exchange}', exchangeLabelNow)}
            </span>
          )}
        </Field>

        {/* Направление сделки: Long (сбор фандинга) / Short (сбор падений).
            Отключённые админом направления (п.7) скрыты — см. DirectionToggle. */}
        <DirectionToggle
          value={tradeMode}
          onChange={handleTradeModeChange}
          t={t}
          recommended={recommendedMode}
          longEnabled={longModeEnabled}
          shortEnabled={shortModeEnabled}
        />

        <TradingModeToggle
          mode={selectedPreset}
          onChange={setSelectedPreset}
          onCustomModeChange={setIsCustomMode}
          setLeverage={setLeverage}
          setOrdersCount={setOrdersCount} // Передаем управление ордерами
          setMinFundingRate={setMinFundingRate}
          setGlobalTp={setGlobalTp}
          setGlobalSl={setGlobalSl}
          t={t}
        />

        <Field label={t.addBotName}>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              // Только английские буквы, цифры и пробелы: остальное молча
              // отфильтровываем, а подсказку показываем, чтобы пользователь
              // понял, почему символ «не набрался».
              const filtered = e.target.value.replace(/[^A-Za-z0-9 ]/g, '')
              setNameEnError(e.target.value !== filtered)
              setName(filtered)
            }}
            placeholder={t.addBotNamePh}
            style={{
              ...inputStyle,
              border: (nameExists || nameEnError)
                ? '1px solid rgba(255,59,48,0.5)'
                : '1px solid rgba(255,255,255,0.08)',
            }}
          />
          {nameExists && (
            <span style={{ display: 'block', fontSize: 11, color: '#FF3B30', marginTop: 5 }}>
              {t.botNameExists}
            </span>
          )}
          {nameEnError && (
            <span style={{ display: 'block', fontSize: 11, color: '#FF3B30', marginTop: 5 }}>
              {t.addBotNameEn}
            </span>
          )}
        </Field>

        <Field label={t.addBotExchange}>
          <ExchangeDropdown
            value={exchange}
            onChange={setExchange}
            takenExchanges={takenExchangeIds}
            appConfig={appConfig}
            t={t}
          />
          {/* Биржа ушла «в разработку»/скрыта уже после открытия формы. */}
          {exchangeLocked && (
            <span style={{ display: 'block', fontSize: 11, color: '#FF3B30', marginTop: 5 }}>
              🔒 {t.botsExchangeUnavailable}
            </span>
          )}
        </Field>

        {/* ── Algorithm params ── */}
        <SectionDivider label={t.addBotAlgoTitle} />

        {/* Leverage: quick-select + manual input */}
        <Field label={t.addBotLeverage}>
          {/* Quick-select buttons */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {LEVERAGE_OPTIONS.map(lv => (
              <button
                key={lv}
                type="button"
                onClick={() => {
                  // Плечо доступно всегда, вне custom-режима. НЕ сбрасываем selectedPreset:
                  // иначе выбор плеча выключал режим «Пользовательские» уже после его
                  // включения, и бот создавался как «Системные» (п.2 task.txt).
                  setLeverage(lv)
                }}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 10,
                  fontSize: 12, fontWeight: 700,
                  background: leverage === lv
                    ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
                    : 'rgba(255,255,255,0.05)',
                  color: leverage === lv ? '#fff' : '#636366',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                {lv}x
              </button>
            ))}
          </div>
          {/* Manual input 1–10 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <NumberInput
                value={leverage}
                onChange={v => {
                  // Как и в quick-select выше: ручной ввод плеча не должен выключать
                  // режим «Пользовательские» (см. п.2 task.txt).
                  const n = parseInt(v)
                  if (!isNaN(n)) setLeverage(Math.min(10, Math.max(1, n)))
                }}
                placeholder="2"
                min={1}
                step={1}
              />
            </div>
            <span style={{ fontSize: 13, color: '#636366', fontWeight: 600, flexShrink: 0 }}>
              ×&nbsp;(1—10)
            </span>
          </div>
        </Field>

        {/* Количество фандингов (Token) */}
        <Field label={t.addBotOrders} tooltip={t.tooltipOrders}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => setOrdersCount(n)}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 10,
                  fontSize: 14, fontWeight: 700,
                  background: ordersCount === n
                    ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
                    : 'rgba(255,255,255,0.05)',
                  color: ordersCount === n ? '#fff' : '#636366',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                }}
              >
                {n}
              </button>
            ))}
          </div>
        </Field>

        {/* Min funding rate */}
        <Field label={t.addBotMinFunding} tooltip={t.tooltipMinFunding}>
          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            {/* Минус стоит заранее и не стирается (2026-10-21): юзер пишет
                только цифру, в поле лежит модуль значения. */}
            <div style={{
              padding: '12px 0 12px 14px',
              borderRadius: '12px 0 0 12px',
              background: isCustomMode ? '#141414' : 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRight: 'none',
              color: isCustomMode ? '#fff' : '#636366',
              fontSize: 14, fontWeight: 700,
              display: 'flex', alignItems: 'center', userSelect: 'none',
              opacity: isCustomMode ? 1 : 0.5,
            }}>
              −
            </div>
            <input
              type="text"
              inputMode="decimal"
              value={minFundingRate}
              onChange={e => {
                // ВАЖНО: НЕ сбрасываем selectedPreset здесь. Это поле доступно
                // только когда isCustomMode уже true (т.е. пользователь уже
                // нажал "Пользовательские"). Если сбросить selectedPreset при
                // вводе, finalTradingMode при отправке станет 'safe' вместо
                // 'custom', и введённое значение будет проигнорировано.
                setMinFundingRate(fundingDraft(e.target.value))
              }}
              placeholder={defaultMinFunding}
              disabled={!isCustomMode}
              style={{
                flex: 1, padding: '12px 14px 12px 4px',
                borderRadius: 0,
                background: isCustomMode ? '#141414' : 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderLeft: 'none',
                borderRight: 'none',
                color: isCustomMode ? '#fff' : '#636366',
                fontSize: 14, fontWeight: 600,
                outline: 'none', boxSizing: 'border-box',
                cursor: isCustomMode ? 'text' : 'not-allowed',
                opacity: isCustomMode ? 1 : 0.5,
              }}
            />
            <div style={{
              padding: '12px 16px',
              borderRadius: '0 12px 12px 0',
              background: '#141414',
              border: '1px solid rgba(255,255,255,0.08)',
              borderLeft: 'none',
              color: '#8E8E93', fontSize: 14, fontWeight: 600,
              display: 'flex', alignItems: 'center',
            }}>
              %
            </div>
          </div>
        </Field>

        {/* Max funding rate (task.txt, 2026-09-16): необязательная НИЖНЯЯ
            граница диапазона ставок. Пусто = ограничения нет. Доступно только
            в режиме пользовательских настроек, как и Min funding rate. */}
        <Field label={t.addBotMaxFunding} tooltip={t.tooltipMaxFunding}>
          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            {/* Минус стоит заранее и не стирается (2026-10-21), как и у верхней
                границы. Пустое поле = «нижней границы нет». */}
            <div style={{
              padding: '12px 0 12px 14px',
              borderRadius: '12px 0 0 12px',
              background: isCustomMode ? '#141414' : 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRight: 'none',
              color: isCustomMode ? '#fff' : '#636366',
              fontSize: 14, fontWeight: 700,
              display: 'flex', alignItems: 'center', userSelect: 'none',
              opacity: isCustomMode ? 1 : 0.5,
            }}>
              −
            </div>
            <input
              type="text"
              inputMode="decimal"
              value={maxFundingRate}
              onChange={e => {
                // Как и для MinFundingRate: НЕ сбрасываем selectedPreset — поле
                // доступно только когда isCustomMode уже true.
                setMaxFundingRate(fundingDraft(e.target.value))
              }}
              disabled={!isCustomMode}
              placeholder={defaultMaxFunding}
              style={{
                flex: 1, padding: '12px 14px 12px 4px',
                borderRadius: 0,
                background: isCustomMode ? '#141414' : 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderLeft: 'none',
                borderRight: 'none',
                color: isCustomMode ? '#fff' : '#636366',
                fontSize: 14, fontWeight: 600,
                outline: 'none', boxSizing: 'border-box',
                cursor: isCustomMode ? 'text' : 'not-allowed',
                opacity: isCustomMode ? 1 : 0.5,
              }}
            />
            <div style={{
              padding: '12px 16px',
              borderRadius: '0 12px 12px 0',
              background: '#141414',
              border: '1px solid rgba(255,255,255,0.08)',
              borderLeft: 'none',
              color: '#8E8E93', fontSize: 14, fontWeight: 600,
              display: 'flex', alignItems: 'center',
            }}>
              %
            </div>
          </div>
          {fundingRangeError && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#FF3B30', lineHeight: 1.35 }}>
              {fundingRangeError}
            </div>
          )}
        </Field>

        {/* Global volume */}
        <Field label={t.addBotGlobalVolume} tooltip={
          <span>
            <span style={{ display: 'block', marginBottom: 8 }}>{t.tooltipGlobalVolume}</span>
            <span style={{ display: 'block', height: 1, background: 'rgba(255,255,255,0.12)', margin: '0 0 8px' }} />
            <span style={{ display: 'block' }}>{t.tooltipGlobalVolumePart2}</span>
          </span>
        }>
          <input
            type="text"
            inputMode="decimal"
            value={globalVolume}
            onChange={(e) => {
              // Разрешаем только цифры и точку
              const val = e.target.value.replace(/[^0-9.]/g, '');
              setGlobalVolume(val);
            }}
            onBlur={clampVolume}
            placeholder="0"
            style={{
              ...inputStyle,
              border: volumeBelowMin
                ? '1px solid rgba(255,59,48,0.5)'
                : '1px solid rgba(255,255,255,0.08)',
            }}
          />
          {volumeBelowMin && (
            <span style={{ display: 'block', fontSize: 11, color: '#FF3B30', marginTop: 5 }}>
              {t.addBotVolumeMin}
            </span>
          )}
        </Field>

        {/* Take Profit — global. В режиме SHORT объяснение своё (уровень падения
            цены ниже фандинга = 1% + 0.3%), в LONG — прежнее. Стоп-лосс один и тот же. */}
        <Field label={t.addBotTakeProfit} tooltip={tradeMode === 'short' ? t.tooltipTakeProfitShort : t.tooltipTakeProfit}>
          <div style={{ position: 'relative' }}>
            <input
              type="number"
              value={globalTp}
              onChange={e => {
                // Не сбрасываем selectedPreset — см. комментарий у поля фандинга выше.
                setGlobalTp(e.target.value)
              }}
              step="0.01"
              min={0}
              placeholder="0.5"
              disabled={!isCustomMode}
              style={{
                ...inputStyle,
                paddingRight: 28,
                border: isCustomMode ? '1px solid rgba(52,199,89,0.35)' : '1px solid rgba(255,255,255,0.08)',
                background: isCustomMode ? '#141414' : 'rgba(255,255,255,0.03)',
                color: isCustomMode ? '#fff' : '#636366',
                cursor: isCustomMode ? 'text' : 'not-allowed',
                opacity: isCustomMode ? 1 : 0.5,
              }}
            />
            <span style={{ ...pctSuffix, color: isCustomMode ? '#34D399' : '#636366' }}>%</span>
          </div>
        </Field>

        {/* Stop Loss — global */}
        <Field label={t.addBotStopLoss} tooltip={t.tooltipStopLoss}>
          <div style={{ position: 'relative' }}>
            <input
              type="number"
              value={globalSl}
              onChange={e => {
                // Не сбрасываем selectedPreset — см. комментарий у поля фандинга выше.
                setGlobalSl(e.target.value)
              }}
              step="0.01"
              min={0}
              placeholder="1.0"
              disabled={!isCustomMode}
              style={{
                ...inputStyle,
                paddingRight: 28,
                border: isCustomMode ? '1px solid rgba(255,59,48,0.35)' : '1px solid rgba(255,255,255,0.08)',
                background: isCustomMode ? '#141414' : 'rgba(255,255,255,0.03)',
                color: isCustomMode ? '#fff' : '#636366',
                cursor: isCustomMode ? 'text' : 'not-allowed',
                opacity: isCustomMode ? 1 : 0.5,
              }}
            />
            <span style={{ ...pctSuffix, color: isCustomMode ? '#FF3B30' : '#636366' }}>%</span>
          </div>
        </Field>

        <button
          onClick={handleLaunch}
          disabled={!canSubmit || isCreating}
          style={{
            marginTop: 8, width: '100%', padding: '14px', borderRadius: 14,
            background: (canSubmit && !isCreating)
              ? 'linear-gradient(135deg, #34D399 0%, #10B981 100%)'
              : 'rgba(255,255,255,0.06)',
            color: (canSubmit && !isCreating) ? '#fff' : '#636366',
            fontSize: 15, fontWeight: 700, border: 'none',
            cursor: (canSubmit && !isCreating) ? 'pointer' : 'default', 
            transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
          }}
        >
          {isCreating ? (
            <>
              <svg className="animate-spin" style={{ width: 18, height: 18 }} viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t.creating}
            </>
          ) : (
            t.addBotLaunch
          )}
        </button>

        {launchError && (
          <p style={{
            marginTop: 8, fontSize: 12, color: '#FF3B30',
            textAlign: 'center', lineHeight: 1.4,
          }}>
            {launchError}
          </p>
        )}

      </div>
    </Modal>
  )
}